import { useTranslate } from '@ur-solutions/react-hooks'
import { Title, Toggle } from '@/components_deprecated'
import { ModuleOptionsValue } from '@/modules/companies/consts'
import type {
  ModuleOptionNode,
  ModuleOptions,
} from '@/modules/companies/types.graphql'
import React, { useEffect, useEffectEvent, useState } from 'react'
import styled from 'styled-components'
import { useDebounce, useModuleConfig } from '@/utils/hooks'
import { useModuleOptionMutations } from '../util'

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

interface OptionToggleProps {
  option: ModuleOptionNode
}

export const OptionToggle: React.FC<OptionToggleProps> = ({ option }) => {
  /*
   * The logic for a mutation on a Toggle component isn't straight forward,
   * since the Toggle's state should follow the value of the active field on
   * the option. Therefore we need to keep a local state for the Toggle value
   * locally, so we can immidiately change state here for a crisp UX, while
   * we send the mutation away.
   *
   * This is done with the use of useEffects checking if all the module configs
   * are loaded, that the local value is changed from the last known DB value
   * and that the user hasn't changed its mind rapidly (the use of a debeounced
   * value). If there is a diff, and the value keeps being different after the
   * debounced timer has gone by, then we send the patch mutation.
   *
   * We never get a proper read from the server on the final value, but the
   * values will keep in sync this way.
   *
   */
  const translations = useTranslate({
    names: {
      countPause: 'settings.timesheets.count-pause-as-part-of-total-work',
      overtime: 'settings.timesheets.alternative-overtime-option',
      projects: 'settings.timesheets.projects',
      periodicOvertime: 'settings.timesheets.periodic-overtime',
      mergeTimesheetsWithActivities:
        'settings.timesheets.merge-timesheets-with-activities',
      departmentMandatory: 'settings.timesheets.department-mandatory',
      requireSeverity: 'settings.issues.require-severity',
      automaticallyCreateIssueFromInfringements:
        'settings.issues.create-issue-from-infringements',
      salaryCodeInRegistration:
        'settings.timesheets.salary-code-in-registration',
      userCanCloseOwnInfringement:
        'settings.infringements.user-can-close-own-infringement',
      recieptFunctionality: 'settings.reciept-functionality',
      simpleDietOption: 'settings.simple-diet-option',
      dietCommentMandatory: 'settings.diet-comment-mandatory',
      // Summary
      summary: {
        driving: 'activities.driving',
        otherWork: 'common.other-work',
        pause: 'common.pause',
        overtime: 'activities.sum-overtime',
        available: 'activities.availability-time',
      },
    },
  })

  const {
    moduleConfig,
    moduleLoading,
    getModuleOption,
    getModuleOptionStatus,
  } = useModuleConfig(option.moduleConfig.name)
  const moduleOption = getModuleOption(option.name)
  const optionActive = getModuleOptionStatus(option.name)
  const [checked, setChecked] = useState<boolean>(optionActive)
  const [moduleLoaded, setModuleLoaded] = useState<boolean>(false)
  const debouncedValue = useDebounce<boolean>(checked, 400)
  const { patch } = useModuleOptionMutations()

  function handleToggleClick(value: boolean) {
    setChecked(value)
  }

  const handleModuleLoaded = useEffectEvent(
    (optionActove: boolean, moduleLoaded: boolean) => {
      setChecked(optionActove)
      setModuleLoaded(moduleLoaded)
    }
  )

  useEffect(() => {
    if (!!moduleConfig && !moduleLoading && !moduleLoaded)
      handleModuleLoaded(optionActive, true)
  }, [moduleLoading, moduleConfig, moduleLoaded, optionActive])

  useEffect(() => {
    if (
      checked === debouncedValue &&
      debouncedValue !== optionActive &&
      !!moduleOption &&
      !moduleLoading &&
      moduleLoaded
    ) {
      void patch({
        variables: {
          id: moduleOption?.id ?? '',
          input: {
            active: debouncedValue,
          },
        },
      })
    }
  }, [
    checked,
    debouncedValue,
    moduleLoading,
    moduleConfig,
    moduleLoaded,
    moduleOption,
    optionActive,
    patch,
  ])

  function getName(moduleName: ModuleOptions) {
    switch (moduleName) {
      case ModuleOptionsValue.TIMESHEETS_OVERTIME_OPTIONS:
        return translations.names.overtime
      case ModuleOptionsValue.TIMESHEETS_COUNT_PAUSE_IN_TOTAL:
        return translations.names.countPause
      case ModuleOptionsValue.TIMESHEETS_PROJECTS:
        return translations.names.projects
      case ModuleOptionsValue.TIMESHEETS_DAY_INTERVAL_OVERTIME:
        return translations.names.periodicOvertime
      case ModuleOptionsValue.MERGE_TIMESHEETS_WITH_ACTIVITIES:
        return translations.names.mergeTimesheetsWithActivities
      case ModuleOptionsValue.TIMESHEETS_DEPARTMENT_SELECT_REQUIRED:
        return translations.names.departmentMandatory
      case ModuleOptionsValue.ISSUES_REQUIRE_SEVERITY:
        return translations.names.requireSeverity
      case ModuleOptionsValue.CREATE_ISSUE_FROM_INFRINGEMENTS:
        return translations.names.automaticallyCreateIssueFromInfringements
      case ModuleOptionsValue.TIMESHEETS_SALARY_CODE_IN_REGISTRATION:
        return translations.names.salaryCodeInRegistration
      case ModuleOptionsValue.INFRINGEMENTS_USER_CAN_CLOSE_OWN_INFRINGEMENT:
        return translations.names.userCanCloseOwnInfringement
      case ModuleOptionsValue.ACTIVITIES_RECEIPT_FUNCTIONALITY:
        return translations.names.recieptFunctionality
      case ModuleOptionsValue.ACTIVITIES_SIMPLE_DIET_OPTION:
        return translations.names.simpleDietOption
      case ModuleOptionsValue.ACTIVITIES_DIET_COMMENT_MANDATORY:
        return translations.names.dietCommentMandatory
      case ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_AVAILABILITY_TIME:
        return translations.names.summary.available
      case ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_OTHER_WORK:
        return translations.names.summary.otherWork
      case ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_TOTAL_OVERTIME:
        return translations.names.summary.overtime
      case ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_DRIVING:
        return translations.names.summary.driving
      case ModuleOptionsValue.ACTIVITIES_SUMMARY_SHOW_PAUSE:
        return translations.names.summary.pause
      default:
        return moduleName
    }
  }

  return (
    <ToggleWrapper>
      <Title.H3>{getName(option.name)}</Title.H3>
      <Toggle
        value={checked}
        thumbBackground="secondary"
        onIconProps={{
          icon: 'check',
        }}
        offIconProps={{
          icon: 'times',
        }}
        onChange={handleToggleClick}
      />
    </ToggleWrapper>
  )
}
